import React, { useState, useRef, useMemo, useContext, useEffect, useCallback } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import containerClasses from '@/orderForm/style.module.scss';
import OrderIcon from '@/components/OrderIcon/OrderIcon';
import 'bootstrap/dist/css/bootstrap.css';
import ApiContext from '@/contexts/ApiContext';
import UserContext from '@/contexts/UserContext';
import Loading from '@/Loading';
import FormError from '@/orderForm/FormError';
import { useGetOrderFormEdit, useOrderFormMutations } from '@/orderForm/hooks/useOrderForm';
import OrderForm from '@/orderForm/OrderForm/OrderForm';
import OrderFormPageFooter from '@/orderForm/OrderForm/OrderFormPage/OrderFormPageFooter';
import OrderFormPageHeader from '@/orderForm/OrderForm/OrderFormPage/OrderFormPageHeader';
import { generateUiSchema } from '@/orderForm/utils/uiSchemaGenerator';
import deepMerge from '@/orderForm/utils/deepMerge';
import PropTypes from 'prop-types';

const hiddenObjectField = (childKey) => ({
  'ui:widget': 'hidden',
  'ui:hidden': true,
  ...(childKey ? { [childKey]: { 'ui:widget': 'hidden', 'ui:hidden': true } } : {}),
});

const removeSchemaProperties = (schema, keys) => {
  if (!schema || !Array.isArray(keys)) return;

  keys.forEach((key) => {
    if (schema.properties) delete schema.properties[key];
    if (schema.definitions) delete schema.definitions[key];
  });

  if (Array.isArray(schema.required)) {
    schema.required = schema.required.filter((key) => !keys.includes(key));
  }
};

const EditOrderFormPage = ({ 
  cancelEditOrder, 
  clientPanel, 
  inspectionIcon, 
  successfulOrderEdition, 
  updateOrderItem: updateOrderItemProp 
}) => {
  const { orderId } = useParams();
  const intl = useIntl();
  const me = useContext(UserContext);
  const formRef = useRef(null);

  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState(0);
  const [apiError, setApiError] = useState(false);

  const { data: order, isLoading: isQueryLoading, isFetching, isError } = useGetOrderFormEdit(orderId);
  const mutations = useOrderFormMutations(orderId);
  const alertedRef = useRef(false);

  useEffect(() => {
    if (order && !order.editable && !alertedRef.current) {
      alertedRef.current = true;
      alert(intl.formatMessage({ id: 'MESSAGE.OFFICIAL_REQUIRED' }));
      cancelEditOrder();
    }
  }, [order, intl, cancelEditOrder]);



  const { schema, uiSchema, uiSchemaDisabled } = useMemo(() => {
    if (!order) return { schema: {}, uiSchema: {}, uiSchemaDisabled: {} };

    const generatedUiSchema = generateUiSchema({
      status: order.status,
      inspectionType: order.type,
      clientPanel,
      schema: order.schema,
      me,
    });

    const generatedUiSchemaDisabled = generateUiSchema({
      status: 'DISABLED',
      inspectionType: order.type,
      clientPanel,
      schema: order.schema,
      me,
    });

    const isClientRole = clientPanel || me?.profile?.role === 'client';
    const schema = JSON.parse(JSON.stringify(order.schema));
    const isMarketplace = order.type?.startsWith('marketplace_');
    const hasBudgetDuringInspectionSectionAllowed =
      me?.features?.includes('FORM_BUDGET_DURING_INSPECTION') && order.type === 'saida';

    if (!hasBudgetDuringInspectionSectionAllowed) {
      removeSchemaProperties(schema, ['budgetDuringInspection']);
    }

    if (order.type === 'acompanhamentoObraMM') {
      removeSchemaProperties(schema, isClientRole
        ? ['additionals', 'accessInformation']
        : ['keys', 'cost', 'additionals', 'accessInformation', 'accompaniedInspection', 'recommendation']);
    } else {
      removeSchemaProperties(schema, ['construction_details', 'solicitations']);
    }

    if (order.type === 'cautelarQueirozGalvao') {
      removeSchemaProperties(schema, ['accessInformation', 'accompaniedInspection']);
    } else {
      removeSchemaProperties(schema, ['location_references']);
    }

    if (isClientRole) {
      generatedUiSchema.additionalCosts = {
        'ui:order': ['price', 'description'],
        price: {
          ...generatedUiSchema.additionalCosts?.price,
          'ui:disabled': true,
        },
        description: {
          ...generatedUiSchema.additionalCosts?.description,
          'ui:disabled': true,
        },
      };

      generatedUiSchema.clients = hiddenObjectField('client');
      generatedUiSchema.franchisees = hiddenObjectField('franchisee');
      generatedUiSchemaDisabled.clients = hiddenObjectField('client');
      generatedUiSchemaDisabled.franchisees = hiddenObjectField('franchisee');
    }

    // For franchisees (non-clients), always hide the franchisee selector.
    // generateUiSchema already does this via clientPanel=false, but enforce here as a safeguard.
    if (!isClientRole) {
      generatedUiSchema.franchisees = hiddenObjectField('franchisee');
      generatedUiSchemaDisabled.franchisees = hiddenObjectField('franchisee');
    }

    if (isClientRole && schema.properties && !isMarketplace) {
      if (!schema.properties.details) {
        schema.properties.details = { type: 'object', properties: {} };
      }
      if (schema.properties.details.properties) {
        schema.properties.details.properties.price = schema.properties.details.properties.price || { type: 'number', title: 'price' };
      }

      if (!schema.properties.additionalCosts) {
        schema.properties.additionalCosts = { type: 'object', properties: {} };
      }
      if (schema.properties.additionalCosts.properties) {
        schema.properties.additionalCosts.properties.price = schema.properties.additionalCosts.properties.price || { type: 'number', title: 'price' };
      }
    }

    if (schema.properties && !schema.properties.inspection_type) {
      schema.properties.inspection_type = { type: 'string' };
    }

    return { 
      schema, 
      uiSchema: generatedUiSchema, 
      uiSchemaDisabled: generatedUiSchemaDisabled 
    };
  }, [order, clientPanel, me]);

  const handleSubmit = useCallback(() => {
    setSubmitted(true);
    formRef.current.submitButton.click();
  }, []);

  const handleCancel = useCallback(() => {
    const confMessage = intl.formatMessage({ id: 'confirmation.cancelEditOrder' });
    if (window.confirm(confMessage)) cancelEditOrder();
  }, [intl, cancelEditOrder]);

  const onSubmit = useCallback(async ({ formData: submittedData }) => {
    if (mutations.saveOrder.isPending) return;
    
    const cleanData = { ...submittedData };
    if (cleanData.recommendation) delete cleanData.recommendation;

    const isMarketplaceType = ['docusign', 'fichacerta', 'bry', 'visitaDigital', 'fotosDeCaptacao', 'captura360'].includes(cleanData.type);
    if (isMarketplaceType) {
      cleanData.action = 'correct';
    }

    try {
      const res = await mutations.saveOrder.mutateAsync(cleanData);
      if (updateOrderItemProp) updateOrderItemProp(res);
      successfulOrderEdition(orderId);
    } catch (err) {
      if (err.response?.data?.error === "'edit' action is not allowed with the current context.") {
        setApiError('INSPECTION_NOT_COPIED_ERROR');
      } else {
        setApiError('UNKNOWN_GENERIC_ERROR');
      }
    }
  }, [
    mutations.saveOrder.isPending,
    mutations.saveOrder.mutateAsync,
    updateOrderItemProp,
    successfulOrderEdition,
    orderId,
  ]);

  const isSaving = mutations.saveOrder.isPending;

  const handleSecondButton = handleSubmit;
  const textSecondButton = 'BUTTON.SAVE';
  let footerContent;

  if (errors > 0) {
    footerContent = <FormError><FormattedMessage id="orderForm.footerError" values={{ errors }} /></FormError>;
  }

  if (apiError) {
    footerContent = <FormError><FormattedMessage id={apiError} /></FormError>;
  }

  if (isError) return <FormError><FormattedMessage id="UNKNOWN_ERROR" /></FormError>;
  if (isQueryLoading || (isFetching && !order)) return <Loading clientPanel={clientPanel} />;
  if (!order || !order.editable) return null;

  return (
    <ApiContext.Provider value={{ status: isSaving ? 'WORKING' : 'IDLE' }}>
      <div className="mx-[1vw] bg-white py-[0.5vw] text-[14px]">
          <OrderFormPageHeader
            inspectionIcon={inspectionIcon || <OrderIcon orderType={order.type} fontSize="calc(16px + 0.8vw)" />}
            text="orderForm.editOrder"
          />

          <div className={`w-full font-['Open_Sans'] font-bold text-[12px] ${containerClasses.orderFormContainer}`}>
            <OrderForm
              key={orderId}
              updateOrderItem={updateOrderItemProp}
              ref={formRef}
              formData={order?.data}
              schema={schema}
              uiSchema={uiSchema}
              submitted={submitted}
              orderType={order.type}
              onSubmit={onSubmit}
              handleError={setErrors}
              handleLoading={() => {}}
              isNewOrder={false}
            />
          </div>

          <OrderFormPageFooter
            handleFirstButton={handleCancel}
            handleSecondButton={handleSecondButton}
            textFirstButton="BUTTON.CANCEL"
            textSecondButton={textSecondButton}
          >
            {footerContent}
          </OrderFormPageFooter>
      </div>
    </ApiContext.Provider>
  );
};

EditOrderFormPage.propTypes = {
  cancelEditOrder: PropTypes.func.isRequired,
  clientPanel: PropTypes.bool.isRequired,
  inspectionIcon: PropTypes.object,
  successfulOrderEdition: PropTypes.func.isRequired,
  updateOrderItem: PropTypes.func,
};

EditOrderFormPage.defaultProps = {
  inspectionIcon: null,
  updateOrderItem: undefined,
};

export default EditOrderFormPage;
